/**
 * Tooltip Component - Helpful hints for Tammy
 * 
 * Shows helpful popup hints on hover (desktop only).
 * Hidden on mobile devices to avoid clutter.
 */

import { useState, ReactNode } from 'react';
import { HiQuestionMarkCircle } from 'react-icons/hi';

interface TooltipProps {
  /** The content to show in the tooltip */
  content: string | ReactNode;
  /** Optional position - use 'top-right' or 'bottom-right' for buttons near screen edges */
  position?: 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** Optional custom className */
  className?: string;
  /** Children element that triggers the tooltip */
  children: ReactNode;
}

export default function Tooltip({ 
  content, 
  position = 'top',
  className = '',
  children 
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);

  // Position classes - adjusted for better edge handling
  // Using right-0 instead of left-1/2 for elements near right edge
  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    // Special positions for edge elements
    'top-left': 'bottom-full left-0 mb-2',
    'top-right': 'bottom-full right-0 mb-2',
    'bottom-left': 'top-full left-0 mt-2',
    'bottom-right': 'top-full right-0 mt-2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-800',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-800',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-800',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-800',
    'top-left': 'top-full left-4 border-t-gray-800',
    'top-right': 'top-full right-4 border-t-gray-800',
    'bottom-left': 'bottom-full left-4 border-b-gray-800',
    'bottom-right': 'bottom-full right-4 border-b-gray-800',
  };

  return (
    <div 
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      
      {/* Tooltip - only visible on desktop (md and up) */}
      {isVisible && (
        <div 
          className={`hidden md:block absolute z-50 ${positionClasses[position]} pointer-events-none`}
          role="tooltip"
        >
          <div className="bg-gray-800 text-white text-sm rounded-lg px-3 py-2 shadow-lg max-w-xs whitespace-normal">
            {typeof content === 'string' ? (
              <p>{content}</p>
            ) : (
              content
            )}
          </div>
          {/* Arrow */}
          <div 
            className={`absolute w-0 h-0 border-4 ${arrowClasses[position]} ${
              position.startsWith('top') || position.startsWith('bottom') 
                ? 'border-l-transparent border-r-transparent' 
                : 'border-t-transparent border-b-transparent'
            }`}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Info Icon with Tooltip - Quick way to add help hints
 */
interface InfoTooltipProps {
  content: string | ReactNode;
  className?: string;
}

export function InfoTooltip({ content, className = '' }: InfoTooltipProps) {
  return (
    <Tooltip content={content} className={className}>
      <HiQuestionMarkCircle className="w-4 h-4 text-gray-400 hover:text-purple-500 transition-colors cursor-help" />
    </Tooltip>
  );
}
